import {queries} from '@/shared/integrations/zero'
import {ListUsersInput} from '@cypro/engine/queries'
import {User} from '@cypro/engine/schema'
import {type GetPageQuery, type GetSingleQuery} from '@cypro/ui/utils/zero-virtualizer'
import {useCallback, useMemo, useState} from 'react'
import {getFullName, UserNameFormat} from '../functions'

export type UserStartRow = Pick<User, 'id' | 'createdAt' | 'firstName' | 'lastName' | 'username' | 'name' | 'status'>

export interface UseGetInfiniteUsersConfig extends Omit<Partial<ListUsersInput>, 'cursor'> {
  nameFormat?: UserNameFormat
}

export function useGetInfiniteUsers({
  sort = {dir: 'asc', field: 'username'},
  filters,
  limit = 25,
  nameFormat,
}: UseGetInfiniteUsersConfig) {
  const [search, setSearch] = useState('')

  const listContextParams = useMemo(
    () => ({sort, filters: {status: ['active'] as const, ...filters}, limit, search}),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [
      sort?.dir,
      sort?.field,
      filters?.groups,
      filters?.name,
      filters?.sections,
      filters?.wards,
      filters?.groupTypes,
      limit,
      search,
    ]
  )

  // @ts-expect-error no idea why this throws an error but it works
  const getPageQuery: GetPageQuery<User, UserStartRow> = useCallback(
    ({limit: pageLimit, start, dir}) => {
      const cursor =
        start?.id && start?.createdAt
          ? {
              id: start.id,
              direction: dir,
              createdAt: start.createdAt,
              firstName: start.firstName || undefined,
              lastName: start.lastName || undefined,
              username: start.username || undefined,
              status: start.status ?? undefined,
            }
          : undefined

      return {
        query: queries.users.listUsers({
          cursor,
          limit: pageLimit,
          sort,
          filters: {status: ['active'] as const, ...filters, name: search || filters?.name},
        }),
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [listContextParams]
  )

  // @ts-expect-error no idea why this throws an error but it works
  const getSingleQuery: GetSingleQuery<User> = useCallback(
    ({id}) => ({
      query: queries.users.getUserById(id),
    }),
    []
  )

  const toStartRow = useCallback(
    (user: User): UserStartRow => ({
      id: user.id,
      createdAt: user.createdAt,
      firstName: user.firstName,
      lastName: user.lastName,
      username: user.username,
      name: user.name,
      status: user.status,
    }),
    []
  )

  const transformToSelectItem = useCallback(
    (user: User) => ({label: getFullName(user, nameFormat), value: user.id}),
    [nameFormat]
  )

  return {
    search,
    onSearchChange: setSearch,
    getPageQuery,
    getSingleQuery,
    toStartRow,
    listContextParams,
    transformToSelectItem,
  }
}
